import type { GeomKind } from "@bezpart-board/shared";
import type { SceneObject } from "./SceneHost";
import { frameHitBounds, isFrameObject } from "./frame/frameContext";
import { isVolumeGeomKind, volumeShapePolylines } from "./geometry/volumeShapes";
import { isPathObject, type PathPoint } from "./pathGeometry";
import { getSegmentDisplayPoints, isSegmentObject } from "./segmentGeometry";
import { objectBounds, type Bounds } from "./selectionBounds";
import { pathStrokeWidth } from "../lib/board/sceneStyle";
import {
  objectRotationRad,
  pointInObjectSpace,
  rotatePoint,
  rotatedObjectBounds,
} from "./transform/rotate";
import { spatialCandidates } from "./spatialIndex";
import { compareZIndex, zIndexKey } from "./zIndex";

function distancePointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  return Math.hypot(px - projX, py - projY);
}

function pointInRect(x: number, y: number, r: Bounds): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function segmentsCross(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number
): boolean {
  const abx = bx - ax;
  const aby = by - ay;
  const cdx = dx - cx;
  const cdy = dy - cy;
  const den = abx * cdy - aby * cdx;
  if (Math.abs(den) < 1e-9) return false;
  const acx = cx - ax;
  const acy = cy - ay;
  const t = (acx * cdy - acy * cdx) / den;
  const u = (acx * aby - acy * abx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

function segmentIntersectsRect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  r: Bounds
): boolean {
  if (pointInRect(ax, ay, r) || pointInRect(bx, by, r)) return true;
  const x1 = r.x;
  const y1 = r.y;
  const x2 = r.x + r.w;
  const y2 = r.y + r.h;
  return (
    segmentsCross(ax, ay, bx, by, x1, y1, x2, y1) ||
    segmentsCross(ax, ay, bx, by, x2, y1, x2, y2) ||
    segmentsCross(ax, ay, bx, by, x2, y2, x1, y2) ||
    segmentsCross(ax, ay, bx, by, x1, y2, x1, y1)
  );
}

/** Stroke corridor intersects selection/marquee rect (not the path AABB). */
export function pathIntersectsRect(
  points: PathPoint[],
  rect: Bounds,
  strokeWidth = 3
): boolean {
  if (points.length < 2 || rect.w <= 0 || rect.h <= 0) return false;
  const pad = Math.max(0, strokeWidth / 2);
  const r: Bounds =
    pad > 0
      ? { x: rect.x - pad, y: rect.y - pad, w: rect.w + pad * 2, h: rect.h + pad * 2 }
      : rect;
  for (let i = 1; i < points.length; i++) {
    const [ax, ay] = points[i - 1]!;
    const [bx, by] = points[i]!;
    if (segmentIntersectsRect(ax, ay, bx, by, r)) return true;
  }
  return false;
}

export function pointNearPath(
  x: number,
  y: number,
  points: PathPoint[],
  radius: number,
  strokeWidth = 3
): boolean {
  const slop = Math.max(radius, strokeWidth / 2 + radius);
  for (let i = 1; i < points.length; i++) {
    const [ax, ay] = points[i - 1]!;
    const [bx, by] = points[i]!;
    if (distancePointToSegment(x, y, ax, ay, bx, by) <= slop) return true;
  }
  return false;
}

function isVolumeGeomObject(
  obj: SceneObject
): obj is SceneObject & { type: "geom"; kind: string; w: number; h: number } {
  return obj.type === "geom" && typeof obj.kind === "string" && isVolumeGeomKind(obj.kind);
}

function volumePolylinesWorld(obj: SceneObject & { kind: string; w: number; h: number }): PathPoint[][] {
  const w = obj.w;
  const h = obj.h;
  const local = volumeShapePolylines(obj.kind as GeomKind, w, h);
  const rot = objectRotationRad(obj);
  if (Math.abs(rot) < 1e-6) {
    return local.map((seg) =>
      seg.map(([lx, ly]) => [obj.x + lx, obj.y + ly] as PathPoint)
    );
  }
  const cx = obj.x + w / 2;
  const cy = obj.y + h / 2;
  return local.map((seg) =>
    seg.map(([lx, ly]) => {
      const [wx, wy] = rotatePoint(obj.x + lx, obj.y + ly, cx, cy, rot);
      return [wx, wy] as PathPoint;
    })
  );
}

function polylinesIntersectRect(
  polylines: PathPoint[][],
  rect: Bounds,
  strokeWidth: number
): boolean {
  for (const pts of polylines) {
    if (pathIntersectsRect(pts, rect, strokeWidth)) return true;
  }
  return false;
}

function pointNearPolylines(
  x: number,
  y: number,
  polylines: PathPoint[][],
  radius: number,
  strokeWidth: number
): boolean {
  for (const pts of polylines) {
    if (pointNearPath(x, y, pts, radius, strokeWidth)) return true;
  }
  return false;
}

export function objectHitAt(obj: SceneObject, x: number, y: number, radius: number): boolean {
  if (obj.locked) return false;
  if (isPathObject(obj)) {
    return pointNearPath(x, y, obj.points, radius, pathStrokeWidth(obj));
  }
  if (isSegmentObject(obj)) {
    const pts = getSegmentDisplayPoints(obj);
    return pointNearPath(x, y, pts, radius, pathStrokeWidth(obj));
  }
  if (isVolumeGeomObject(obj)) {
    return pointNearPolylines(
      x,
      y,
      volumePolylinesWorld(obj),
      radius,
      pathStrokeWidth(obj)
    );
  }
  const b = isFrameObject(obj) ? frameHitBounds(obj) : objectBounds(obj);
  // Точку переводим в систему объекта: у повёрнутой картинки попадание
  // считалось по тому прямоугольнику, где она лежала ДО поворота.
  const [px, py] = pointInObjectSpace(obj, x, y);
  return (
    px >= b.x - radius &&
    px <= b.x + b.w + radius &&
    py >= b.y - radius &&
    py <= b.y + b.h + radius
  );
}

/** Marquee / box selection: strokes hit by ink, other objects by AABB. */
export function objectIntersectsSelectionRect(obj: SceneObject, rect: Bounds): boolean {
  if (obj.locked) return false;
  if (isPathObject(obj)) {
    return pathIntersectsRect(obj.points, rect, pathStrokeWidth(obj));
  }
  if (isSegmentObject(obj)) {
    return pathIntersectsRect(getSegmentDisplayPoints(obj), rect, pathStrokeWidth(obj));
  }
  if (isVolumeGeomObject(obj)) {
    return polylinesIntersectRect(volumePolylinesWorld(obj), rect, pathStrokeWidth(obj));
  }
  // Рамкой выделения ловим по габариту повёрнутого объекта.
  const b = rotatedObjectBounds(obj);
  return b.x < rect.x + rect.w && b.x + b.w > rect.x && b.y < rect.y + rect.h && b.y + b.h > rect.y;
}

/** Выше ли a объекта b по отрисовке (zIndex, при равенстве — позже в массиве). */
function isAboveInStack(
  a: SceneObject,
  aIndex: number,
  b: SceneObject,
  bIndex: number
): boolean {
  const z = compareZIndex(zIndexKey(a), zIndexKey(b));
  if (z !== 0) return z > 0;
  return aIndex > bIndex;
}

/** Верхний объект в точке (как на экране: max zIndex, не порядок в массиве сцены). */
export function hitTestTopObject(
  objects: SceneObject[],
  x: number,
  y: number,
  radius: number
): SceneObject | null {
  const candidates = spatialCandidates(objects, x, y, radius);
  const indexById = new Map<string, number>();
  for (let i = 0; i < objects.length; i++) {
    indexById.set(objects[i]!.id, i);
  }

  let best: SceneObject | null = null;
  let bestIndex = -1;
  for (const obj of candidates) {
    if (!objectHitAt(obj, x, y, radius)) continue;
    const idx = indexById.get(obj.id) ?? 0;
    if (!best || isAboveInStack(obj, idx, best, bestIndex)) {
      best = obj;
      bestIndex = idx;
    }
  }
  return best;
}

/** All objects touched by eraser circle (any z-order). */
export function hitTestAllObjects(
  objects: SceneObject[],
  x: number,
  y: number,
  radius: number
): SceneObject[] {
  const candidates = spatialCandidates(objects, x, y, radius);
  return candidates.filter((obj) => objectHitAt(obj, x, y, radius));
}
